import { ApiResponse } from '@/types';
import api from './client';

// Player Comparison API
export const playerComparisonAPI = {
  compareMultiplePlayers: async (playerIds: string[]): Promise<ApiResponse<{
    players: Array<{
      id: string;
      name: string;
      position: string;
      age: number;
      injury_status?: string;

      // Performance metrics
      avg_rating_30d?: number;
      avg_rating_7d?: number;
      performance_trend?: 'improving' | 'declining' | 'stable';
      total_reports_30d?: number;
      last_performance_report?: string;

      // Training metrics
      attendance_rate_30d?: number;
      avg_rpe_30d?: number;
      avg_sleep_score_30d?: number;
      avg_soreness_score_30d?: number;
      avg_mood_score_30d?: number;
      total_srpe_load_30d?: number;
      sessions_attended_30d?: number;
      sessions_scheduled_30d?: number;

      // Load management
      current_acwr?: number;
      load_risk?: 'low' | 'moderate' | 'high' | 'very_high' | 'insufficient_data';
      acute_load?: number;
      chronic_load?: number;

      // Scouting metrics
      avg_shooting?: number;
      avg_passing?: number;
      avg_pace?: number;
      avg_dribbling?: number;
      avg_defending?: number;
      avg_physical?: number;
      scouting_reports_count: number;
      last_scouting_report?: string;

      // Performance stats
      avg_performance_rating?: number;
      total_goals?: number;
      total_assists?: number;
      avg_minutes_played?: number;
      avg_distance_covered?: number;
      performance_reports_count: number;
    }>;
    comparison_date: string;
  }>> => {
    const response = await api.post('/player-comparison/compare', { player_ids: playerIds });
    return { data: response.data };
  },

  getPositionBenchmarks: async (position: string): Promise<ApiResponse<{
    position: string;
    player_count: number;

    // Performance benchmarks
    avg_rating?: number;
    rating_25th_percentile?: number;
    rating_75th_percentile?: number;

    // Training benchmarks
    avg_attendance?: number;
    avg_rpe?: number;
    avg_load?: number;

    // Load management benchmarks
    avg_acwr?: number;
    high_risk_count: number;

    // Scouting benchmarks
    avg_shooting?: number;
    avg_passing?: number;
    avg_pace?: number;
    avg_dribbling?: number;
    avg_defending?: number;
    avg_physical?: number;

    // Performance stats benchmarks
    avg_match_rating?: number;
    avg_goals_per_match?: number;
    avg_assists_per_match?: number;
    avg_minutes_per_match?: number;
    avg_distance_per_match?: number;
  }>> => {
    const response = await api.get(`/player-comparison/position/${position}/benchmarks`);
    return response.data;
  },

  getSimilarPlayers: async (playerId: string, limit: number = 5): Promise<ApiResponse<{
    player_id: string;
    similar_players: Array<{
      id: string;
      name: string;
      position: string;
      age: number;
      avg_rating_30d?: number;
      avg_rpe_30d?: number;
      current_acwr?: number;
      overall_rating?: number;
      similarity_score: number;
    }>;
    limit: number;
  }>> => {
    const response = await api.get(`/player-comparison/player/${playerId}/similar?limit=${limit}`);
    return response.data;
  },

  getPlayerRadarData: async (playerId: string): Promise<ApiResponse<{
    id: string;
    name: string;
    position: string;

    // Percentile scores (0-100)
    shooting_percentile?: number;
    passing_percentile?: number;
    pace_percentile?: number;
    dribbling_percentile?: number;
    defending_percentile?: number;
    physical_percentile?: number;

    // Raw averages
    avg_shooting?: number;
    avg_passing?: number;
    avg_pace?: number;
    avg_dribbling?: number;
    avg_defending?: number;
    avg_physical?: number;
  }>> => {
    const response = await api.get(`/player-comparison/player/${playerId}/radar`);
    return response.data;
  },

  getTeamComparisonSummary: async (position?: string, team?: string): Promise<ApiResponse<{
    players: Array<{
      id: string;
      name: string;
      position: string;
      age: number;
      position_rank: number;
      position_total: number;
      avg_rating_30d?: number;
      performance_trend?: 'improving' | 'declining' | 'stable';
      attendance_rate_30d?: number;
      avg_rpe_30d?: number;
      current_acwr?: number;
      load_risk?: 'low' | 'moderate' | 'high' | 'very_high' | 'insufficient_data';
      status_indicator: 'injured' | 'high_risk' | 'low_attendance' | 'declining' | 'improving' | 'stable';
    }>;
    by_position: Record<string, Array<{
      id: string;
      name: string;
      position: string;
      age: number;
      position_rank: number;
      position_total: number;
      avg_rating_30d?: number;
      performance_trend?: 'improving' | 'declining' | 'stable';
      attendance_rate_30d?: number;
      avg_rpe_30d?: number;
      current_acwr?: number;
      load_risk?: 'low' | 'moderate' | 'high' | 'very_high' | 'insufficient_data';
      status_indicator: string;
    }>>;
    filters: {
      position?: string;
      team?: string;
    };
  }>> => {
    const params = new URLSearchParams();
    if (position) params.append('position', position);
    if (team) params.append('team', team);

    const response = await api.get(`/player-comparison/team/summary?${params.toString()}`);
    return response.data;
  },
};
